import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Input, Select, Switch, Checkbox, Spin, Empty } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { apiUsuariosInstance, authHeaderUsuarios } from "../../redux/actions/usuarios/usuarios";
import { MODULOS, ACTIONS } from "../../constants/modulos";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const ACTION_COLORS = {
  CREATE: { label: "Creado", color: "#16a34a", bg: "#dcfce7" },
  UPDATE: { label: "Editado", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Eliminado", color: "#dc2626", bg: "#fee2e2" },
};
const getActionConfig = (a) =>
  ACTION_COLORS[String(a || "").toUpperCase()] || { label: a || "Evento", color: "#64748b", bg: "#f1f5f9" };

export default function UsuarioDetallePage() {
  const { idUser } = useParams();
  const navigate = useNavigate();

  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");
  const [roles, setRoles] = useState([]);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [permisos, setPermisos] = useState({});
  const [loadingPerm, setLoadingPerm] = useState(false);
  const [savingPerm, setSavingPerm] = useState(false);

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchUsuario = useCallback(() => {
    setLoading(true);
    apiUsuariosInstance.get(`/usuarios/${idUser}`, { headers: authHeaderUsuarios() })
      .then((res) => setUsuario(res.data))
      .catch(() => toast.error("No se pudo cargar el usuario"))
      .finally(() => setLoading(false));
  }, [idUser]);

  useEffect(() => { fetchUsuario(); }, [fetchUsuario]);

  useEffect(() => {
    apiUsuariosInstance.get("/usuarios/roles", { headers: authHeaderUsuarios() })
      .then((res) => setRoles(res.data?.items ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== "permisos") return;
    setLoadingPerm(true);
    apiUsuariosInstance.get(`/usuarios/${idUser}/permisos`, { headers: authHeaderUsuarios() })
      .then((res) => setPermisos(res.data || {}))
      .catch(() => toast.error("No se pudieron cargar los permisos"))
      .finally(() => setLoadingPerm(false));
  }, [activeTab, idUser]);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiUsuariosInstance.get(`/usuarios/${idUser}/actividad`, { headers: authHeaderUsuarios() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idUser]);

  const isActive = usuario && (usuario.active === 1 || usuario.active === true);

  const startEdit = () => {
    setEditForm({
      id_rol: usuario.id_rol,
      nombre: usuario.nombre,
      email_recuperacion: usuario.email_recuperacion || "",
      active: isActive,
      password: "",
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!editForm.nombre?.trim()) return toast.warning("El nombre es obligatorio");
    setSaving(true);
    try {
      await apiUsuariosInstance.patch(`/usuarios/${idUser}`, {
        id_rol: editForm.id_rol,
        nombre: editForm.nombre.trim(),
        email_recuperacion: editForm.email_recuperacion?.trim() || null,
        active: !!editForm.active,
        password: editForm.password || null,
      }, { headers: authHeaderUsuarios() });
      toast.success("Usuario actualizado correctamente");
      setEditing(false);
      fetchUsuario();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const togglePermiso = (modKey, action, checked) => {
    setPermisos((prev) => {
      const cur = { ...(prev[modKey] || {}) };
      cur[action] = checked;
      if (action === "modulo" && !checked) ACTIONS.forEach((a) => { cur[a.key] = false; });
      if (action !== "modulo" && checked) cur.modulo = true;
      return { ...prev, [modKey]: cur };
    });
  };

  const handleSavePermisos = async () => {
    setSavingPerm(true);
    try {
      const res = await apiUsuariosInstance.put(`/usuarios/${idUser}/permisos`, { permisos }, { headers: authHeaderUsuarios() });
      setPermisos(res.data?.permisos || permisos);
      toast.success("Permisos guardados");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron guardar los permisos");
    } finally {
      setSavingPerm(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!usuario) {
    return <div className="cd-main" style={{ padding: 40 }}><Empty description="Usuario no encontrado" /></div>;
  }

  const tabBtn = (key, icon, label) => (
    <button className={`cd-tab-btn ${activeTab === key ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab(key)}>
      <i className={`fa-solid ${icon}`} /> {label}
    </button>
  );

  const field = (label, value) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: "#64748b" }}>{label}</div>
      <div style={{ fontWeight: 500 }}>{value || "—"}</div>
    </div>
  );

  const labelStyle = { fontSize: 12, color: "#64748b" };

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(usuario.nombre || "—").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-at" /> {usuario.usuario}</span>
                <span className="cd-meta-item"><i className="fa-solid fa-user-shield" /> {usuario.rol || "Sin rol"}</span>
                <span className="cd-meta-item">
                  <i className="fa-solid fa-circle" style={{ color: isActive ? "#16a34a" : "#dc2626" }} /> {isActive ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>
            <div className="cd-header-actions">
              <Button onClick={() => navigate("/app/usuarios")}>Volver</Button>
            </div>
          </div>
          <div className="cd-header-tabs">
            {tabBtn("datos", "fa-file-lines", "Informacion")}
            {tabBtn("permisos", "fa-shield-halved", "Permisos")}
            {tabBtn("actividad", "fa-clock", "Actividad")}
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-user"></i></div>
              <h2 className="cd-card-title">Datos de la cuenta</h2>
              {!editing ? (
                <Button size="small" icon={<i className="fa-solid fa-pen" />} onClick={startEdit} style={{ marginLeft: "auto" }}>Editar</Button>
              ) : (
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <Button size="small" icon={<CloseOutlined />} onClick={() => setEditing(false)} disabled={saving}>Cancelar</Button>
                  <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}
                    style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Guardar</Button>
                </div>
              )}
            </div>
            {!editing ? (
              <div>
                {field("Nombre", usuario.nombre)}
                {field("Usuario", usuario.usuario)}
                {field("Rol", usuario.rol)}
                {field("Correo de recuperacion", usuario.email_recuperacion)}
                {field("Creado", fmtDatetime(usuario.datetime))}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12, maxWidth: 480 }}>
                <div>
                  <div style={labelStyle}>Nombre</div>
                  <Input value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} />
                </div>
                <div>
                  <div style={labelStyle}>Rol</div>
                  <Select style={{ width: "100%" }} value={editForm.id_rol} onChange={(v) => setEditForm({ ...editForm, id_rol: v })}
                    options={roles.map((r) => ({ value: r.id_rol, label: r.nombre }))} />
                </div>
                <div>
                  <div style={labelStyle}>Correo de recuperacion</div>
                  <Input value={editForm.email_recuperacion} onChange={(e) => setEditForm({ ...editForm, email_recuperacion: e.target.value })} />
                </div>
                <div>
                  <div style={labelStyle}>Nueva contrasena (dejar vacio para no cambiarla)</div>
                  <Input.Password value={editForm.password} autoComplete="new-password" onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Switch checked={!!editForm.active} onChange={(v) => setEditForm({ ...editForm, active: v })} /> Cuenta activa
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "permisos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-shield-halved"></i></div>
              <h2 className="cd-card-title">Permisos por modulo</h2>
              <Button size="small" type="primary" icon={<SaveOutlined />} loading={savingPerm} onClick={handleSavePermisos}
                style={{ marginLeft: "auto", backgroundColor: "#f90336", borderColor: "#f90336" }}>Guardar permisos</Button>
            </div>
            {usuario.rol && String(usuario.rol).toLowerCase().startsWith("admin") && (
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
                Este usuario es Administrador: tiene acceso total sin importar lo guardado aqui.
              </div>
            )}
            {loadingPerm ? <Spin /> : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ padding: "8px 6px" }}>Modulo</th>
                      {ACTIONS.map((a) => <th key={a.key} style={{ padding: "8px 6px", textAlign: "center" }}>{a.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULOS.map((m) => (
                      <tr key={m.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "8px 6px" }}>{m.icon} <span style={{ marginLeft: 8 }}>{m.label}</span></td>
                        {ACTIONS.map((a) => (
                          <td key={a.key} style={{ padding: "8px 6px", textAlign: "center" }}>
                            <Checkbox checked={!!permisos[m.key]?.[a.key]} onChange={(e) => togglePermiso(m.key, a.key, e.target.checked)} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
